import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AlertTriangle, Brain, IndianRupee, Sparkles, TrendingDown } from 'lucide-react'
import founderKpiService from '../../services/founderKpiService'
import salesDashboardService from '../../services/salesDashboardService'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { formatCompactCurrency, formatCurrency } from '../../utils/currencyFormat'
import { currentFyLabel } from '../../utils/financialYearMonths'
import './Dashboard.css'

const getCurrentMonth = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

const money = (amount, currency = 'INR') => formatCurrency(amount || 0, currency, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const changeLabel = (row) => {
  if (row?.isNew) return 'New'
  if (row?.change === null || row?.change === undefined) return 'No prior month'
  const value = Number(row.change)
  if (!Number.isFinite(value)) return ''
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
}

const changeTone = (row) => {
  if (row?.isNew) return 'new'
  const value = Number(row?.change)
  if (!Number.isFinite(value) || value === 0) return 'flat'
  return value > 0 ? 'up' : 'down'
}

const Dashboard = () => {
  const navigate = useNavigate()
  const [founderKpis, setFounderKpis] = useState(null)
  const [founderKpisLoading, setFounderKpisLoading] = useState(false)
  const [founderKpiMonth, setFounderKpiMonth] = useState(getCurrentMonth())
  const [pulse, setPulse] = useState(null)
  const [pulseLoading, setPulseLoading] = useState(true)
  const [chartCurrency, setChartCurrency] = useState('INR')

  useEffect(() => {
    const loadPulse = async () => {
      try {
        setPulseLoading(true)
        const response = await salesDashboardService.getDashboardStats()
        if (response?.success) setPulse(response.data)
        else if (!response?._errorHandled) toast.error(response?.message || 'Failed to load the overview')
      } catch (error) {
        console.error('Overview pulse:', error)
        if (!error._errorHandled) toast.error(error.response?.data?.message || 'Failed to load the overview')
      } finally {
        setPulseLoading(false)
      }
    }
    loadPulse()
  }, [])

  useEffect(() => {
    const loadFounderKpis = async () => {
      try {
        setFounderKpisLoading(true)
        const response = await founderKpiService.getFounderKpis(founderKpiMonth)
        if (response?.success) setFounderKpis(response.data)
      } catch (error) {
        console.error('Founder KPIs:', error)
      } finally {
        setFounderKpisLoading(false)
      }
    }
    loadFounderKpis()
  }, [founderKpiMonth])

  useEffect(() => {
    const trends = pulse?.revenueTrends || {}
    const codes = pulse?.currencies?.length ? pulse.currencies : Object.keys(trends)
    const withRevenue = codes.find((code) => (trends[code] || []).some((month) => Number(month.revenue) > 0))
    if (withRevenue) setChartCurrency(withRevenue)
  }, [pulse])

  const monthRows = pulse?.billing?.thisMonth || []
  const receivables = pulse?.billing?.receivables || []
  const overdueRows = pulse?.billing?.overdue || []
  const chartCodes = pulse?.currencies?.length ? pulse.currencies : Object.keys(pulse?.revenueTrends || {})
  const activeChart = chartCodes.includes(chartCurrency) ? chartCurrency : (chartCodes[0] || 'INR')
  const chartSeries = pulse?.revenueTrends?.[activeChart] || (activeChart === 'INR' ? pulse?.revenueTrend : [])
  const metrics = pulse?.metrics || {}
  const burn = Number(founderKpis?.monthlyBurn) || 0
  const runwayKnown = founderKpis?.runwayMonths != null && burn > 0

  return (
    <div className="sales-dashboard dashboard-page">
      <div className="page-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="page-title">
          <h1>Overview</h1>
          <p>Cash and expenses stay in INR. Invoices stay in their own currency until the money is received.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-2" onClick={() => navigate('/dashboard/ai-analytics')}>
            <Brain size={14} /> AI Analytics
          </button>
          <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-2 dashboard-ai-assistant-btn" onClick={() => navigate('/ai-assistant')}>
            <Sparkles size={14} /> AI Assistant
          </button>
        </div>
      </div>

      <div className="app-overview">
        <section className="app-panel">
          <div className="app-section-head">
            <div>
              <h2>Cash and expenses</h2>
              <p>Bank and cash balances, and approved expenses. These books are in INR.</p>
            </div>
            <input
              type="month"
              className="form-control app-month"
              value={founderKpiMonth}
              onChange={(event) => setFounderKpiMonth(event.target.value)}
              aria-label="Expense month"
            />
          </div>
          <div className="app-stat-grid">
            <article className="app-stat">
              <span className="app-stat-icon"><IndianRupee size={16} /></span>
              <span className="app-stat-label">Cash available</span>
              <strong>{founderKpisLoading ? '...' : money(founderKpis?.cashTotal, 'INR')}</strong>
              <span className="app-stat-note">Cash and bank balance</span>
            </article>
            <article className="app-stat">
              <span className="app-stat-icon"><TrendingDown size={16} /></span>
              <span className="app-stat-label">Approved expenses</span>
              <strong>{founderKpisLoading ? '...' : money(burn, 'INR')}</strong>
              <span className="app-stat-note">For the selected month</span>
            </article>
            <article className="app-stat">
              <span className="app-stat-label">Runway</span>
              <strong>{founderKpisLoading ? '...' : runwayKnown ? `${founderKpis.runwayMonths} months` : '—'}</strong>
              <span className="app-stat-note">
                {founderKpisLoading ? 'Checking expenses' : runwayKnown ? 'Cash divided by this month’s expenses' : 'No approved expenses, so runway is not calculated'}
              </span>
            </article>
          </div>
          {founderKpis?.pendingApprovals > 0 && (
            <p className="app-note">
              {founderKpis.pendingApprovals} draft expense{founderKpis.pendingApprovals === 1 ? '' : 's'} still waiting for approval.
            </p>
          )}
          <div className="app-realised">
            <div>
              <span>Received in INR</span>
              <strong>{pulseLoading ? '...' : money(pulse?.receipts?.thisMonthInINR, 'INR')}</strong>
              <p>This month. Foreign receipts use the rate entered when the payment was recorded.</p>
            </div>
            <div>
              <span>This financial year</span>
              <strong>{pulseLoading ? '...' : money(pulse?.receipts?.financialYearInINR, 'INR')}</strong>
              <p>{pulse?.financialYear ? `FY ${pulse.financialYear}` : `FY ${currentFyLabel()}`}</p>
            </div>
          </div>
          {pulse?.receipts?.awaitingRate > 0 && (
            <p className="app-note">
              {pulse.receipts.awaitingRate} foreign receipt{pulse.receipts.awaitingRate === 1 ? '' : 's'} still need a conversion rate, so {pulse.receipts.awaitingRate === 1 ? 'it is' : 'they are'} not in this rupee total.
            </p>
          )}
        </section>

        <section className="app-panel">
          <div className="app-section-head">
            <div>
              <h2>Billed this month</h2>
              <p>Approved tax invoices. A dollar invoice is not added into the rupee total.</p>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => navigate('/sales?view=overview')}>
              Sales overview
            </button>
          </div>
          <div className="app-facts">
            <div><span>Invoices</span><strong>{metrics.invoicesThisMonth || 0}</strong></div>
            <div><span>Domestic</span><strong>{metrics.domesticInvoicesThisMonth || 0}</strong></div>
            <div><span>Export</span><strong>{metrics.exportInvoicesThisMonth || 0}</strong></div>
            <div><span>Overdue</span><strong>{metrics.overdueCount || 0}</strong></div>
          </div>
          {pulseLoading ? (
            <div className="app-empty">Loading invoices...</div>
          ) : monthRows.length === 0 ? (
            <div className="app-empty">No approved tax invoices this month or last month.</div>
          ) : (
            <div className="app-currency-grid">
              {monthRows.map((row) => (
                <article key={row.currency} className="app-currency-card">
                  <div className="app-card-top">
                    <span className="app-code">{row.currency}</span>
                    <span className={`app-change app-change-${changeTone(row)}`}>{changeLabel(row)}</span>
                  </div>
                  <div className="app-amount">{money(row.billed, row.currency)}</div>
                  <div className="app-last">Last month {money(row.previousBilled, row.currency)}</div>
                  <div className="app-meta">
                    <span>{row.invoices || 0} invoice{row.invoices === 1 ? '' : 's'}</span>
                    <span>{row.exportInvoices || 0} export</span>
                    <span>Tax {money(row.tax, row.currency)}</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <div className="app-split">
          <section className="app-panel">
            <div className="app-section-head">
              <div>
                <h2>Still to collect</h2>
                <p>Open balances on billed invoices.</p>
              </div>
            </div>
            {pulseLoading ? (
              <div className="app-empty">Loading balances...</div>
            ) : receivables.length === 0 ? (
              <div className="app-empty">Nothing outstanding.</div>
            ) : (
              <ul className="app-money-list">
                {receivables.map((row) => {
                  const late = overdueRows.find((item) => item.currency === row.currency)
                  return (
                    <li key={row.currency}>
                      <span className="app-code">{row.currency}</span>
                      <span className="app-money-figure">{money(row.outstanding, row.currency)}</span>
                      <span className="app-money-note">
                        {row.invoices} open{late ? ` · ${money(late.outstanding, row.currency)} overdue` : ''}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section className="app-panel">
            <div className="app-section-head">
              <div>
                <h2>Needs attention</h2>
                <p>Drafts, invoices due soon, and overdue balances.</p>
              </div>
            </div>
            {pulseLoading ? (
              <div className="app-empty">Loading...</div>
            ) : pulse?.actionItems?.length ? (
              <ul className="app-attention">
                {pulse.actionItems.map((item) => (
                  <li key={item.id || item.title}>
                    <AlertTriangle size={16} />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="app-empty">No drafts waiting, and nothing due in the next 7 days.</div>
            )}
          </section>
        </div>

        <section className="app-panel">
          <div className="app-section-head">
            <div>
              <h2>Billed by month</h2>
              <p>
                {pulse?.financialYear ? `FY ${pulse.financialYear}` : `FY ${currentFyLabel()}`}
                {' · '}
                {money((chartSeries || []).reduce((sum, month) => sum + (Number(month.revenue) || 0), 0), activeChart)}
                {' in '}
                {activeChart}
              </p>
            </div>
            {chartCodes.length > 0 && (
              <div className="app-switch" role="tablist" aria-label="Chart currency">
                {chartCodes.map((code) => (
                  <button key={code} type="button" className={code === activeChart ? 'is-active' : ''} onClick={() => setChartCurrency(code)}>
                    {code}
                  </button>
                ))}
              </div>
            )}
          </div>
          {pulseLoading ? (
            <div className="app-empty">Loading the year...</div>
          ) : (
            <FyMonthlyBarChart
              series={chartSeries}
              valueKey="revenue"
              formatValue={(value) => formatCompactCurrency(value, activeChart)}
              formatTooltip={(value) => money(value, activeChart)}
              emptyMessage={`No ${activeChart} invoices in this financial year`}
            />
          )}
        </section>
      </div>
    </div>
  )
}

export default Dashboard
